import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { industrySolutions, painSolutions } from '@/mocks/solutionsData';

function useVisible(threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { visible, ref };
}

export default function Solutions() {
  const industryRef = useVisible(0.1);
  const painRef = useVisible(0.1);

  return (
    <main>
      {/* Page Header */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-[#0c2e2e] via-[#1a3d3d] to-[#0c2e2e] overflow-hidden">
        {/* 深色网格纹理 */}
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.25) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.25) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
        {/* 右上光晕 */}
        <div
          className="absolute -top-24 -right-24 w-[600px] h-[600px] rounded-full opacity-[0.15] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(0,174,239,0.5) 0%, transparent 70%)' }}
        />
        {/* 左下光晕 */}
        <div
          className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-[0.12] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(0,174,239,0.3) 0%, transparent 70%)' }}
        />
        {/* 横向光线 */}
        <div className="absolute top-1/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent/10 to-transparent pointer-events-none" />
        <div className="absolute top-2/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent/10 to-transparent pointer-events-none" />
        {/* 浮动小圆点 */}
        <div className="absolute top-24 right-[25%] w-2 h-2 bg-accent/30 rounded-full animate-pulse pointer-events-none" />
        <div className="absolute bottom-28 left-[20%] w-1.5 h-1.5 bg-white/20 rounded-full animate-pulse pointer-events-none" style={{ animationDelay: '1.5s' }} />
        {/* 装饰弧线 */}
        <div className="absolute top-[20%] right-[5%] w-[200px] h-[200px] rounded-full border border-white/5 pointer-events-none" />
        <div className="absolute bottom-[15%] left-[3%] w-[150px] h-[150px] rounded-full border border-white/5 pointer-events-none" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              分销转动销，不同行业不同策略
            </h1>
            <p className="text-sm md:text-base text-white/60 leading-relaxed">
              让营销投入可量化、可追踪、可优化
            </p>
          </div>
        </div>
      </section>

      {/* By Industry */}
      <section ref={industryRef.ref} className="py-16 md:py-24 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="mb-10 md:mb-14">
            <h2
              className={`text-2xl md:text-3xl font-bold text-primary mb-3 transition-all duration-500 ${
                industryRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              按行业
            </h2>
            <p
              className={`text-sm md:text-base text-text-muted transition-all duration-500 delay-100 ${
                industryRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              深耕快消细分领域，提供针对性解决方案
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {industrySolutions.map((sol, index) => (
              <div
                key={index}
                className={`group bg-surface rounded-xl p-5 md:p-6 border border-gray-100 hover:border-accent/30 hover:bg-surface-card transition-all duration-300 ${
                  industryRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${200 + index * 80}ms` }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary rounded-xl">
                    <i className={`${sol.icon} text-xl text-white`} />
                  </div>
                  <h3 className="text-lg font-bold text-primary">{sol.name}</h3>
                </div>
                <div>
                  <span className="text-xs text-accent font-medium">行业痛点</span>
                  <p className="text-sm text-text-secondary mt-1">{sol.pain}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* By Pain Points */}
      <section
        ref={painRef.ref}
        className="py-16 md:py-24 bg-surface-warm"
      >
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="mb-10 md:mb-14">
            <h2
              className={`text-2xl md:text-3xl font-bold text-primary mb-3 transition-all duration-500 ${
                painRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              按痛点
            </h2>
            <p
              className={`text-sm md:text-base text-text-muted transition-all duration-500 delay-100 ${
                painRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              直击快消营销核心难题
            </p>
          </div>

          <div className="space-y-6 md:space-y-8">
            {painSolutions.map((pain, index) => (
              <div
                key={index}
                className={`flex flex-col md:flex-row gap-4 md:gap-8 bg-white rounded-xl p-5 md:p-6 border border-gray-100 hover:border-accent/30 hover:shadow-md transition-all duration-300 ${
                  painRef.visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                }`}
                style={{ transitionDelay: `${200 + index * 120}ms` }}
              >
                <div className="shrink-0">
                  <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-primary rounded-xl">
                    <span className="text-xl md:text-2xl font-bold text-white">{pain.num}</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-primary mb-2">{pain.title}</h3>
                  <p className="text-sm md:text-base text-text-secondary leading-relaxed">{pain.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-surface-warm">
        <div className="w-full px-4 md:px-8 lg:px-12 text-center">
          <h2 className="text-xl md:text-3xl font-bold text-primary mb-4">
            找到适合您的解决方案
          </h2>
          <p className="text-sm md:text-base text-text-muted mb-8 max-w-xl mx-auto">
            告诉我们您的行业和痛点，获取定制化解决方案建议。
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3 bg-accent text-white font-semibold rounded-lg hover:bg-accent-dark transition-colors"
          >
            <i className="ri-calendar-check-line" />
            获取方案建议
          </Link>
        </div>
      </section>
    </main>
  );
}